import { format } from 'date-fns';
import { TZDate, tzName } from '@date-fns/tz';
import type {
  DataFreshnessResponse,
  FreshnessStatus,
  MetricFreshness,
  PlatformFreshnessItem
} from 'shared/src/trpc-types/freshness-types';

export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const FRESHNESS_STATUS_STYLES = {
  fresh: { bg: 'bg-lime-200', text: 'text-lime-800', dot: 'bg-lime-500', label: 'Fresh' },
  stale: { bg: 'bg-amber-200', text: 'text-amber-800', dot: 'bg-amber-500', label: 'Stale' },
  very_stale: { bg: 'bg-red-200', text: 'text-red-800', dot: 'bg-red-500', label: 'Critical' },
  inactive: { bg: 'bg-slate-200', text: 'text-slate-700', dot: 'bg-slate-400', label: 'Idle' },
  unknown: { bg: 'bg-gray-200', text: 'text-gray-600', dot: 'bg-gray-400', label: 'No Data' }
} as const satisfies Record<
  FreshnessStatus,
  { bg: string; text: string; dot: string; label: string }
>;

// Fallback for a status this bundle does not know about — a browser holding an older
// cached bundle can receive a newer status string than it has a style for, and an
// undefined lookup would crash the whole page rather than degrade one badge.
export const UNKNOWN_STATUS_STYLE = FRESHNESS_STATUS_STYLES.unknown;

export function freshnessStatusStyle(
  status: FreshnessStatus
): (typeof FRESHNESS_STATUS_STYLES)[keyof typeof FRESHNESS_STATUS_STYLES] {
  return FRESHNESS_STATUS_STYLES[status] ?? UNKNOWN_STATUS_STYLE;
}

export const NA_STYLE = { bg: 'bg-gray-100', text: 'text-gray-400 italic', label: 'N/A' } as const;

// Severity ranking for the table sort. `inactive` ranks below `fresh`: it is not a
// problem to act on, so it must never outrank a real signal.
export const FRESHNESS_STATUS_SORT_ORDER: Record<FreshnessStatus, number> = {
  very_stale: 0,
  stale: 1,
  fresh: 2,
  inactive: 3,
  unknown: 4
} as const;

export type SummaryPillKey = 'fresh' | 'stale' | 'critical' | 'idle' | 'noData';

export const SUMMARY_KEY_TO_STATUS: Record<SummaryPillKey, FreshnessStatus> = {
  fresh: 'fresh',
  stale: 'stale',
  critical: 'very_stale',
  idle: 'inactive',
  noData: 'unknown'
} as const;

export const FRESHNESS_QUERY_OPTIONS = {
  refetchInterval: 60_000,
  staleTime: 30_000,
  refetchIntervalInBackground: false
} as const;

export function formatTimestampWithTz(date: Date | number): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return `${format(new TZDate(d, BROWSER_TZ), "MMM d, yyyy 'at' h:mm a")} ${tzName(BROWSER_TZ, d, 'short')}`;
}

export type FreshnessBadgeUnit = 'd' | 'm';

export function formatBadgeLabel(
  status: FreshnessStatus,
  amountBehind: number,
  unit: FreshnessBadgeUnit = 'd'
): string {
  const baseLabel = freshnessStatusStyle(status).label;
  if ((status === 'stale' || status === 'very_stale') && amountBehind > 0) {
    return `${baseLabel}: ${amountBehind}${unit}`;
  }
  return baseLabel;
}

export function worstPlatformMetric(platform: PlatformFreshnessItem): {
  status: FreshnessStatus;
  daysBehind: number;
} {
  const imp = platform.impressions;
  if (!platform.video) return { status: imp.status, daysBehind: imp.daysBehind };
  const vid = platform.video;
  const status =
    FRESHNESS_STATUS_SORT_ORDER[imp.status] <= FRESHNESS_STATUS_SORT_ORDER[vid.status]
      ? imp.status
      : vid.status;
  return { status, daysBehind: Math.max(imp.daysBehind, vid.daysBehind) };
}

export type OverallFreshnessState = 'healthy' | 'stale' | 'critical' | 'noData';

export type StatusCounts = {
  fresh: number;
  stale: number;
  critical: number;
  idle: number;
  noData: number;
};

export type FreshnessSortableRow =
  | {
      kind: 'platform';
      name: string;
      status: FreshnessStatus;
      daysBehind: number;
      impressions: MetricFreshness;
      video: MetricFreshness | null;
      lastDataDate: Date | null;
      activeBuyCount: number | undefined;
      expectedDataDate: Date | null | undefined;
    }
  | {
      kind: 'zoho';
      name: string;
      status: FreshnessStatus;
      daysBehind: number;
      lastModified: Date | null;
    }
  | {
      kind: 'activity';
      name: string;
      status: FreshnessStatus;
      minutesBehind: number;
      lastActivityAt: Date | null;
      lastRefreshAt: Date | null;
    };

export type BuildRowsOptions = { includeActivity?: boolean };

export function computeStatusCounts(
  data: DataFreshnessResponse,
  options: BuildRowsOptions = {}
): StatusCounts {
  const allStatuses = [data.zoho.status, ...data.platforms.map(p => worstPlatformMetric(p).status)];
  if (options.includeActivity && data.activity) allStatuses.push(data.activity.status);
  return allStatuses.reduce(
    (acc, s) => {
      if (s === 'fresh') acc.fresh++;
      else if (s === 'stale') acc.stale++;
      else if (s === 'very_stale') acc.critical++;
      else if (s === 'inactive') acc.idle++;
      else acc.noData++;
      return acc;
    },
    { fresh: 0, stale: 0, critical: 0, idle: 0, noData: 0 }
  );
}

// `idle` is intentionally absent: a platform with nothing in flight is not a state
// anyone needs to act on, so it must not drive the page-level banner.
export function computeOverallWorst(counts: StatusCounts): OverallFreshnessState {
  if (counts.critical > 0) return 'critical';
  if (counts.stale > 0) return 'stale';
  if (counts.noData > 0) return 'noData';
  return 'healthy';
}

export function buildSortedRows(
  data: DataFreshnessResponse,
  options: BuildRowsOptions = {}
): FreshnessSortableRow[] {
  const rows: FreshnessSortableRow[] = [
    ...data.platforms.map((p): FreshnessSortableRow => {
      const worst = worstPlatformMetric(p);
      return {
        kind: 'platform',
        name: p.platformName,
        status: worst.status,
        daysBehind: worst.daysBehind,
        impressions: p.impressions,
        video: p.video,
        lastDataDate: p.impressions.maxDataDate,
        activeBuyCount: p.activeBuyCount,
        expectedDataDate: p.expectedDataDate
      };
    }),
    {
      kind: 'zoho',
      name: data.zoho.name,
      status: data.zoho.status,
      daysBehind: data.zoho.daysBehind,
      lastModified: data.zoho.lastModified
    }
  ];

  if (options.includeActivity && data.activity) {
    rows.push({
      kind: 'activity',
      name: data.activity.name,
      status: data.activity.status,
      minutesBehind: data.activity.minutesBehind,
      lastActivityAt: data.activity.lastActivityAt,
      lastRefreshAt: data.activity.lastRefreshAt
    });
  }

  return rows.sort((a, b) => {
    const severityDiff =
      FRESHNESS_STATUS_SORT_ORDER[a.status] - FRESHNESS_STATUS_SORT_ORDER[b.status];
    if (severityDiff !== 0) return severityDiff;
    // Numeric tiebreaker only applies within the same kind — mixing 20 minutes of
    // activity staleness with 2 days of Zoho staleness as raw integers would rank
    // activity above Zoho. Cross-kind ties fall through to name.
    if (a.kind === b.kind) {
      const aBehind = a.kind === 'activity' ? a.minutesBehind : a.daysBehind;
      const bBehind = b.kind === 'activity' ? b.minutesBehind : b.daysBehind;
      const behindDiff = bBehind - aBehind;
      if (behindDiff !== 0) return behindDiff;
    }
    return a.name.localeCompare(b.name);
  });
}
